import { useEffect } from "react";
import { useI18n } from "@/i18n";
import { BUSINESS_SCHEMA } from "@/lib/data";

/**
 * SEO / social / structured-data tags. Uses React 19 native document metadata:
 * <title>, <meta> and <link> tags rendered here are automatically hoisted into
 * <head> and de-duplicated by React, so every route ships exactly one correct
 * title, description, canonical and hreflang set.
 */
export default function Seo({ title, description, image, path = "", jsonLd, noindex = false }) {
  const { lang } = useI18n();
  const enUrl = `https://eewaste.com${path}`;
  const esUrl = `https://eewaste.com/es${path === "/" ? "" : path}`;
  const url = lang === "es" ? esUrl : enUrl;

  // The <html lang> attribute can't be set by rendering, so sync it here.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const providedLd = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const ld = [BUSINESS_SCHEMA, ...providedLd.filter((obj) => obj?.["@id"] !== BUSINESS_SCHEMA["@id"])];

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <link rel="canonical" href={url} />
      {/* hreflang: let Google serve the right language and index both without duplicate-content penalties */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="EWS" />
      <meta property="og:locale" content={lang === "es" ? "es_US" : "en_US"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }} />
      ))}
    </>
  );
}
